<template>
  <article class="card">
    <header class="card-head">
      <div>
        <h3>{{ fichaLabel(ficha) }}</h3>
        <p class="note">Confirmación de Caja: <strong>{{ formatDate(confirmationDate) }}</strong></p>
      </div>
      <button type="button" class="btn ghost" @click="$emit('open', ficha)">Abrir ficha</button>
    </header>

    <section v-if="pendingJobs.length" class="block pending" :aria-labelledby="'pending-work-title-' + ficha.id">
      <h4 :id="'pending-work-title-' + ficha.id">Trabajos nuevos de preparación</h4>
      <p class="note">Solicitudes del ejecutor pendientes de aprobación.</p>
      <ul class="work-list">
        <li v-for="job in pendingJobs" :key="job.id">
          <div>
            <strong>{{ job.rubro || 'Trabajo nuevo' }}</strong>
            <span>{{ job.detalle || '—' }}</span>
            <span v-if="job.agregado && job.agregado.por" class="note">Agregado por {{ job.agregado.por }}</span>
          </div>
          <div class="pending-side">
            <span class="amount">{{ formatJobCost(job) }}</span>
            <div v-if="canReview" class="btns">
              <button type="button" class="btn ghost" :disabled="reviewingJobId !== null" @click="decideJob(job, 'aprobar')">Aprobar</button>
              <button type="button" class="btn danger" :disabled="reviewingJobId !== null" @click="decideJob(job, 'rechazar')">Rechazar</button>
            </div>
          </div>
        </li>
      </ul>
      <p v-if="reviewError" class="note" role="alert">{{ reviewError }}</p>
    </section>

    <div v-if="loading" class="empty" role="status" aria-live="polite"><strong>Consultando trabajos y órdenes…</strong></div>
    <div v-else-if="error" class="empty" role="alert" aria-live="polite">
      <strong>No se pudo consultar el reacondicionamiento.</strong>
      <span>{{ error }}</span>
      <button type="button" class="btn ghost" @click="retry">Reintentar</button>
    </div>

    <template v-else-if="reconditioning">
      <section class="block" :aria-labelledby="'approved-work-title-' + ficha.id">
        <h4 :id="'approved-work-title-' + ficha.id">Trabajos aprobados</h4>
        <p v-if="!approvedJobs.length" class="note">No hay trabajos aprobados para esta ficha.</p>
        <ul v-else class="work-list">
          <li v-for="(job, index) in approvedJobs" :key="job.id || index">
            <div><strong>{{ job.rubro || job.concepto || 'Trabajo aprobado' }}</strong><span>{{ job.detalle || job.descripcion || '—' }}</span></div>
            <span class="amount">{{ formatWorkCost(job) }}</span>
          </li>
        </ul>
      </section>

      <section class="block" :aria-labelledby="'service-orders-title-' + ficha.id">
        <h4 :id="'service-orders-title-' + ficha.id">Órdenes de servicio</h4>
        <p v-if="verificationMessage" class="note" :role="reconditioning.vinculo === 'encontrado' ? 'status' : 'alert'">{{ verificationMessage }}</p>
        <article v-for="order in orders" :key="order.OrdenId" class="order">
          <header class="order-head">
            <h5>Orden {{ order.NumeroOrden || '—' }}</h5>
            <strong>{{ order.abierta ? 'Abierta' : 'Cerrada' }}</strong>
          </header>
          <dl>
            <div><dt>Apertura</dt><dd>{{ formatDate(order.Apertura) }}</dd></div>
            <div><dt>Cierre</dt><dd>{{ order.abierta ? '—' : formatDate(order.Cierre) }}</dd></div>
            <div><dt>Incidentes del cliente</dt><dd class="plain-text">{{ order.IncidentesCliente || '—' }}</dd></div>
            <div><dt>Resultado técnico</dt><dd class="plain-text">{{ order.ResultadoTecnico || '—' }}</dd></div>
          </dl>
        </article>
      </section>

      <div class="btns"><button type="button" class="btn ghost" :disabled="loading" @click="retry">Actualizar órdenes</button></div>
    </template>
  </article>
</template>

<script>
import { money } from '../../../util/tomaUsados'

const validPositiveDate = value => {
  if (value instanceof Date) return Number.isFinite(value.getTime()) && value.getTime() > 0
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 && Number.isFinite(new Date(value).getTime())
  if (typeof value !== 'string' || !value.trim()) return false
  const text = value.trim()
  const timestamp = /^-?\d+(?:\.\d+)?$/.test(text) ? Number(text) : Date.parse(text)
  return Number.isFinite(timestamp) && timestamp > 0 && Number.isFinite(new Date(timestamp).getTime())
}

export default {
  name: 'TomaReconditioningCard',
  props: {
    ficha: { type: Object, required: true },
    sessionContext: { type: String, required: true },
    fetchReconditioning: { type: Function, required: true },
    canReview: { type: Boolean, default: false },
    reviewJob: { type: Function, default: null }
  },
  data () {
    return { reconditioning: null, loading: false, error: '', requestGeneration: 0, reviewingJobId: null, reviewError: '' }
  },
  computed: {
    pendingJobs () {
      const jobs = this.ficha && this.ficha.reacond && Array.isArray(this.ficha.reacond.trabajos)
        ? this.ficha.reacond.trabajos
        : []
      return jobs.filter(job => job && job.aprobacion === 'pendiente')
    },
    confirmationDate () {
      return this.ficha && this.ficha.recepcion && this.ficha.recepcion.aceptacion
        ? this.ficha.recepcion.aceptacion.fecha
        : null
    },
    confirmed () {
      return this.ficha && Number.isSafeInteger(Number(this.ficha.id)) && Number(this.ficha.id) > 0 && validPositiveDate(this.confirmationDate)
    },
    scopeKey () {
      const ficha = this.ficha || {}
      return [this.sessionContext, ficha.id, ficha.version, this.confirmationDate].join('|')
    },
    approvedJobs () {
      return this.reconditioning && Array.isArray(this.reconditioning.trabajos)
        ? this.reconditioning.trabajos.filter(job => job && job.realizar === true)
        : []
    },
    orders () {
      return this.reconditioning && this.reconditioning.vinculo === 'encontrado' && Array.isArray(this.reconditioning.ordenes)
        ? this.reconditioning.ordenes
        : []
    },
    verificationMessage () {
      if (!this.reconditioning) return ''
      if (this.reconditioning.vinculo === 'no_encontrado') return 'No fue posible verificar las órdenes de servicio: no se encontró una unidad vinculada a la patente.'
      if (this.reconditioning.vinculo === 'ambiguo') return 'No fue posible verificar las órdenes de servicio: más de una unidad coincide con la patente.'
      if (this.reconditioning.vinculo !== 'encontrado' || !Array.isArray(this.reconditioning.ordenes)) return 'No fue posible verificar las órdenes de servicio.'
      if (!this.reconditioning.ordenes.length) return 'No hay órdenes verificadas desde la fecha de confirmación de Caja.'
      return ''
    }
  },
  watch: {
    scopeKey: {
      immediate: true,
      handler () { this.resetAndLoad() }
    }
  },
  beforeDestroy () {
    this.requestGeneration++
  },
  methods: {
    fichaLabel (ficha) {
      const vehicle = ficha && ficha.vehiculo ? ficha.vehiculo : {}
      return [ficha.numero || ficha.id, vehicle.dominio || 'Sin dominio', ficha.cliente || 'Sin cliente'].join(' · ')
    },
    formatDate (value) {
      if (!validPositiveDate(value)) return '—'
      const date = value instanceof Date ? value : new Date(typeof value === 'string' && /^-?\d+(?:\.\d+)?$/.test(value.trim()) ? Number(value) : value)
      return date.toLocaleString()
    },
    formatWorkCost (job) {
      const amount = job.costoAjustado != null ? job.costoAjustado : job.costo
      return amount == null ? '—' : money(amount)
    },
    formatJobCost (job) {
      return job && job.presup != null ? money(job.presup) : '—'
    },
    async decideJob (job, decision) {
      if (!this.canReview || !this.reviewJob || this.reviewingJobId !== null) return null
      this.reviewingJobId = job.id
      this.reviewError = ''
      try {
        return await this.reviewJob({ ficha: this.ficha, jobId: job.id, decision })
      } catch (error) {
        this.reviewError = error && error.message ? error.message : 'No se pudo revisar el trabajo.'
        return null
      } finally {
        this.reviewingJobId = null
      }
    },
    resetAndLoad () {
      this.requestGeneration++
      this.reconditioning = null
      this.loading = false
      this.error = ''
      if (this.confirmed) return this.loadReconditioning()
    },
    isCurrent (generation, scopeKey) {
      return !this._isDestroyed && generation === this.requestGeneration && scopeKey === this.scopeKey
    },
    async loadReconditioning () {
      if (!this.confirmed || this.loading) return
      const fichaId = Number(this.ficha.id)
      const scopeKey = this.scopeKey
      const generation = ++this.requestGeneration
      this.error = ''
      this.loading = true
      try {
        const result = await this.fetchReconditioning(fichaId)
        if (this.isCurrent(generation, scopeKey)) this.reconditioning = result
      } catch (error) {
        if (this.isCurrent(generation, scopeKey)) this.error = error && error.message ? error.message : 'No se pudo consultar el reacondicionamiento.'
      } finally {
        if (this.isCurrent(generation, scopeKey)) this.loading = false
      }
    },
    async retry () {
      if (!this.confirmed || this.loading) return
      return this.loadReconditioning()
    }
  }
}
</script>

<style scoped>
.card { display: grid; gap: 16px; min-width: 0; padding: 16px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
.card-head, .order-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.card-head h3, .block h4, .order h5 { margin: 0; }
.card-head h3 { overflow-wrap: anywhere; }
.note { margin: 6px 0 0; color: #64748b; }
.block { display: grid; gap: 10px; min-width: 0; }
.work-list { display: grid; gap: 8px; margin: 0; padding: 0; list-style: none; }
.work-list li, .order { min-width: 0; padding: 12px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; }
.work-list li { display: flex; justify-content: space-between; gap: 12px; }
.work-list li div { display: grid; gap: 4px; min-width: 0; }
.pending { padding: 12px; border: 1px solid #fcd34d; border-radius: 10px; background: #fffbeb; }
.pending-side { display: grid; gap: 8px; justify-items: end; }
.amount { white-space: nowrap; font-weight: 700; }
.order { display: grid; gap: 12px; }
.order-head h5 { font-size: 1rem; }
.order-head strong { color: #334155; }
.order dl { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin: 0; }
.order dl div { display: grid; gap: 4px; min-width: 0; }
.order dt { color: #64748b; font-size: .82rem; }
.order dd { margin: 0; }
.plain-text { white-space: pre-wrap; overflow-wrap: anywhere; }
.empty { display: grid; gap: 10px; justify-items: start; padding: 16px; border-radius: 10px; background: #f8fafc; }
.btns { display: flex; gap: 10px; }
@media (max-width: 560px) { .card-head, .work-list li { flex-direction: column; } }
</style>
